import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { CalendarClock, CalendarRange, CheckCircle2, Clock, MessageSquare, StickyNote, TriangleAlert, User, X, PenLine, Pencil, Check, Loader2, ChevronDown, ChevronLeft, ChevronRight, FolderKanban, FileText, MapPin, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ReviewPanel from "@/components/tracker/ReviewPanel";
import { openTaskEditor } from "@/components/tracker/TaskEditor";
import { handoff } from "@/components/tracker/NoteViewer";
import { guardReview, completeTask, reachesCompletion, needsReviewer, askWithdrawReason } from "@/components/tracker/reviewGuard";
import { useBilling } from "@/context/BillingContext";
import { STATUS_COLORS, PRIORITY_COLORS, STATUSES, isOverdue, formatApiError, slug } from "@/lib/constants";
import { DateField } from "@/components/tracker/DateField";

const fmtDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

const Meta = ({ label, icon: Icon, testid, danger = false, children }) => (
  <div>
    <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/35">{label}</p>
    <div className={`flex items-center gap-1.5 text-sm font-semibold ${danger ? "text-[#FF8B94]" : "text-white/90"}`} data-testid={testid}>
      {Icon && <Icon className={`h-3.5 w-3.5 shrink-0 ${danger ? "text-[#FF8B94]/80" : "text-white/35"}`} />}
      <span className="min-w-0 truncate">{children}</span>
    </div>
  </div>
);

const EASE = [0.22, 1, 0.36, 1];
const BACKDROP = {
  hidden: (instant) => ({ opacity: 0, transition: { duration: instant ? 0 : 0.25 } }),
  shown: { opacity: 1, transition: { duration: 0.25 } },
};
const CARD = {
  enter: { opacity: 0, y: 20, scale: 0.97 },
  shown: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: EASE } },
  behind: { opacity: 0, scale: 0.98, transition: { duration: 0.2, ease: EASE } },
  hidden: (instant) => (instant
    ? { opacity: 0, transition: { duration: 0 } }
    : { opacity: 0, y: 14, scale: 0.97, transition: { duration: 0.3, ease: EASE } }),
};

export default function TaskDetail({ task: taskProp, projectName, projectCode, groupName, ptoWarning = false, canEdit = false, canManage = false, nav = null, onSaved, onOpenDiscussion, onClose }) {
  // Latest copy after a full edit from this window; a new task from the host replaces it.
  const [edited, setEdited] = useState(null);
  useEffect(() => { setEdited(null); }, [taskProp]);
  const task = edited && edited.id === taskProp?.id ? edited : taskProp;
  // Opening the discussion swaps windows over a steady backdrop instead of cross-fading two.
  const [handingOff, setHandingOff] = useState(false);
  const [fullEdit, setFullEdit] = useState(false);
  useEffect(() => { if (taskProp) setHandingOff(false); }, [taskProp]);
  useEffect(() => { setFullEdit(false); }, [taskProp?.id]);
  const replyCount = task ? (task.comment_count || 0) : 0;
  const [editing, setEditing] = useState(false);
  const downOnBackdrop = useRef(false);
  const [prog, setProg] = useState(task?.progress ?? 0);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(task?.status ?? "NOT STARTED");
  const [liveTask, setLiveTask] = useState(null);
  const [editingStatus, setEditingStatus] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [editingDue, setEditingDue] = useState(false);
  const [due, setDue] = useState(task?.due_date ?? "");
  const [savingDue, setSavingDue] = useState(false);
  const has = useBilling()?.has;

  useEffect(() => {
    setProg(task?.progress ?? 0);
    setStatus(task?.status ?? "NOT STARTED");
    setDue(task?.due_date ?? "");
    setEditing(false);
    setEditingStatus(false);
    setEditingDue(false);
    // re-sync only when another task opens or after a save; live updates must not wipe in-progress edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, edited]);

  useEffect(() => {
    if (!task) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || document.querySelector("[data-testid=review-guard], [data-testid=task-modal], [data-testid=completion-guard], [data-radix-popper-content-wrapper]")) return;
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [task, onClose]);

  // Left / right arrows step through the list the task was opened from.
  useEffect(() => {
    if (!task || !nav) return undefined;
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const el = document.activeElement;
      if (el && (["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName) || el.isContentEditable)) return;
      if (fullEdit || document.querySelector("[data-testid=task-modal], [data-testid=review-guard], [data-testid=completion-guard], [data-testid=task-note-viewer]")) return;
      const go = e.key === "ArrowLeft" ? nav.onPrev : nav.onNext;
      if (go) { e.preventDefault(); go(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [task, nav, fullEdit]);

  const overdue = task ? isOverdue({ ...task, due_date: due, status }) : false;

  const saveProgress = () => {
    if (!reachesCompletion({ ...task, status }, prog)) return persistProgress({ progress: prog });
    return completeTask({ ...task, ...liveTask, status }, has, persistProgress);
  };

  const persistProgress = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, body);
      if (data?.status) setStatus(data.status);
      setLiveTask(data);
      toast.success(data?.status === "DONE" ? "Task completed" : data?.status === "REVIEW" ? (has?.("review") ? "Sent for review" : "Moved to Review") : "Progress updated");
      setEditing(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveStatus = async (next) => {
    let body = { status: next };
    const reason = await askWithdrawReason({ ...task, ...liveTask, status }, next, has);
    if (reason === null) return;
    if (reason) body.withdraw_reason = reason;
    if (next === "REVIEW") {
      if (needsReviewer({ ...task, ...liveTask }, has)) { setEditingStatus(false); return; }
      const choice = await guardReview({ progress: prog ?? 0, has });
      if (!choice) return;
      if (choice === "complete") body = { status: next, progress: 100 };
    }
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, body);
      setStatus(next);
      setLiveTask(data);
      if (next === "DONE") setProg(100);
      else if (data && typeof data.progress === "number") setProg(data.progress);
      toast.success(next === "DONE" ? "Marked as done" : `Moved to ${next}`);
      setEditingStatus(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingStatus(false);
    }
  };

  const saveDue = async () => {
    setSavingDue(true);
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { due_date: due || "" });
      toast.success(due ? "Due date updated" : "Due date cleared");
      setEditingDue(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDue(false);
    }
  };

  const statusColor = STATUS_COLORS[status];

  return (
    <AnimatePresence custom={handingOff}>
      {task && (
        <motion.div
          custom={handingOff}
          variants={BACKDROP}
          initial="hidden"
          animate="shown"
          exit="hidden"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (downOnBackdrop.current && e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            custom={handingOff}
            variants={CARD}
            initial="enter"
            animate={fullEdit ? "behind" : "shown"}
            exit="hidden"
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0d0f12] p-6 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9)] sm:p-8"
            data-testid="task-detail"
          >

            {/* Header */}
            <div className="relative flex items-start justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                {canEdit ? (
                  <button
                    onClick={() => setEditingStatus((v) => !v)}
                    className="inline-flex h-7 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 text-[10px] font-black uppercase tracking-wider text-white/90 transition-colors duration-200 hover:border-white/20"
                    data-testid="task-detail-status-edit"
                    title="Change status"
                  >
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
                    {status}
                    <ChevronDown className={`h-3 w-3 text-white/40 transition-transform duration-200 ${editingStatus ? "rotate-180" : ""}`} />
                  </button>
                ) : (
                  <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 text-[10px] font-black uppercase tracking-wider text-white/90">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
                    {status}
                  </span>
                )}
                <span className="inline-flex h-7 items-center rounded-full px-3 text-[10px] font-black uppercase tracking-wider" style={{ backgroundColor: PRIORITY_COLORS[task.priority], color: "#050505" }}>
                  {task.priority}
                </span>
                {overdue && (
                  <span className="inline-flex h-7 items-center gap-1 rounded-full bg-[#FF8B94] px-3 text-[10px] font-black uppercase tracking-wider text-black" data-testid="task-detail-overdue">
                    <TriangleAlert className="h-3 w-3" /> Overdue
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
              {nav && (
                <div className="flex items-center gap-1 rounded-full bg-white/5 p-0.5" data-testid="task-detail-nav">
                  <button
                    onClick={nav.onPrev || undefined}
                    disabled={!nav.onPrev}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-25"
                    aria-label="Previous task"
                    title="Previous task (←)"
                    data-testid="task-detail-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="min-w-[44px] text-center text-[10px] font-bold tabular-nums tracking-wider text-white/45" data-testid="task-detail-nav-count">
                    {nav.index + 1} of {nav.total}
                  </span>
                  <button
                    onClick={nav.onNext || undefined}
                    disabled={!nav.onNext}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white disabled:pointer-events-none disabled:opacity-25"
                    aria-label="Next task"
                    title="Next task (→)"
                    data-testid="task-detail-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
              {canManage && (
                <button
                  onClick={() => {
                    setFullEdit(true);
                    openTaskEditor(task, (fresh) => {
                      if (fresh) { setEdited(fresh); setLiveTask(fresh); }
                      onSaved?.(fresh);
                    }, () => setFullEdit(false));
                  }}
                  className="flex h-8 items-center gap-1.5 rounded-full bg-white/5 px-3 text-[10px] font-black uppercase tracking-wider text-white/60 transition-colors duration-200 hover:bg-[#00FF66]/10 hover:text-[#00FF66]"
                  data-testid="task-detail-edit-btn"
                  title="Edit task"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
              )}
              <button
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white/50 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                data-testid="task-detail-close"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
              </div>
            </div>

            {/* Status picker */}
            <AnimatePresence>
              {editingStatus && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 grid grid-cols-3 gap-2 rounded-2xl border border-white/5 bg-white/[0.02] p-2.5" data-testid="task-detail-status-picker">
                    {STATUSES.map((s) => {
                      const active = s === status;
                      const c = STATUS_COLORS[s];
                      return (
                        <button
                          key={s}
                          onClick={() => saveStatus(s)}
                          disabled={savingStatus}
                          className="flex h-8 items-center justify-center gap-1 rounded-lg px-1 text-[10px] font-black uppercase tracking-wider transition-all duration-150 hover:brightness-110 disabled:opacity-50"
                          style={{
                            backgroundColor: active ? c : `${c}1F`,
                            color: active ? "#050505" : c,
                            border: `1px solid ${c}${active ? "" : "55"}`,
                          }}
                          data-testid={`task-detail-status-option-${slug(s)}`}
                        >
                          {active && <Check className="h-3 w-3 shrink-0" />}
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Title */}
            <h2 className="font-display mt-5 text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl" data-testid="task-detail-title">
              {task.title}
            </h2>

            {ptoWarning && (
              <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[#FFD3B6]/30 bg-[#FFD3B6]/10 p-3.5 text-xs font-semibold text-[#FFD3B6]" data-testid="task-detail-pto-warning">
                <TriangleAlert className="h-4 w-4 shrink-0" />
                Owner is on approved time off during this task&apos;s due week.
              </div>
            )}

            {/* Metadata card */}
            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4 sm:p-5">
              <Meta label="Project" icon={FolderKanban} testid="task-detail-project">
                {projectName || "—"}{projectCode ? <span className="ml-1 font-normal text-white/40">({projectCode})</span> : null}
              </Meta>
              <Meta label="Owner" icon={User} testid="task-detail-owner">{task.owner_name || "Unassigned"}</Meta>
              <Meta label="Document type" icon={FileText} testid="task-detail-doctype">{task.document_type || "—"}</Meta>
              <Meta label="Area" icon={MapPin} testid="task-detail-area">{task.area || "—"}</Meta>
              {groupName && (
                <Meta label="Discipline group" icon={Layers} testid="task-detail-group">
                  <span className="text-[#90C2E7]">{groupName}</span>
                </Meta>
              )}
              <Meta label="Created" icon={Clock} testid="task-detail-created">{fmtDateTime(task.created_at)}</Meta>
              {!groupName && <div aria-hidden="true" />}
              <Meta label="Start date" icon={CalendarRange} testid="task-detail-start">{fmtDate(task.start_date)}</Meta>
              {editingDue ? (
                <div className="col-span-2" data-testid="task-detail-due-editor">
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/35">Due date</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="w-48">
                      <DateField allowPast size="sm" value={due || ""} onChange={setDue} placeholder="No due date" testid="task-detail-due-input" />
                    </div>
                    <button
                      onClick={saveDue}
                      disabled={savingDue}
                      className="flex items-center gap-1.5 rounded-full bg-[#00FF66] px-3.5 py-1.5 text-xs font-bold text-black shadow-[0_0_16px_rgba(0,255,102,0.25)] transition-colors duration-200 hover:bg-[#00cc52] disabled:opacity-60"
                      data-testid="task-detail-due-save"
                    >
                      {savingDue ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Save
                    </button>
                    <button
                      onClick={() => { setEditingDue(false); setDue(task.due_date ?? ""); }}
                      className="rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-bold text-white/60 transition-colors duration-200 hover:border-white/30 hover:text-white"
                      data-testid="task-detail-due-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white/35">Due date</p>
                  <div className={`flex items-center gap-1.5 text-sm font-semibold ${overdue ? "text-[#FF8B94]" : "text-white/90"}`} data-testid="task-detail-due">
                    <CalendarClock className={`h-3.5 w-3.5 shrink-0 ${overdue ? "text-[#FF8B94]/80" : "text-white/35"}`} />
                    <span className="min-w-0 truncate">{fmtDate(due)}</span>
                    {canEdit && !canManage && (
                      <button
                        onClick={() => { setDue(task.due_date ?? ""); setEditingDue(true); }}
                        className="flex h-6 w-6 items-center justify-center rounded-full bg-white/5 text-white/40 transition-colors duration-200 hover:bg-[#00FF66]/15 hover:text-[#00FF66]"
                        data-testid="task-detail-due-edit"
                        aria-label="Reschedule due date"
                        title="Reschedule"
                      >
                        <PenLine className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {status === "DONE" && task.completed_at && (
                <Meta label="Completed" icon={CheckCircle2} testid="task-detail-completed">
                  <span className="text-[#A8E6CF]">{fmtDateTime(task.completed_at)}</span>
                </Meta>
              )}
            </div>

            {/* Progress card */}
            <div className="mt-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4 sm:p-5">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">Progress</p>
                <div className="flex items-center gap-2.5">
                  <span className="font-display text-lg font-black" style={{ color: prog === 100 ? "#A8E6CF" : "#00FF66" }} data-testid="task-detail-progress">{prog}%</span>
                  {canEdit && !editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="flex h-6 w-6 items-center justify-center rounded-full bg-white/5 text-white/40 transition-colors duration-200 hover:bg-[#00FF66]/15 hover:text-[#00FF66]"
                      data-testid="task-detail-progress-edit"
                      aria-label="Update progress"
                      title="Update progress"
                    >
                      <PenLine className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              {editing ? (
                <div className="space-y-3.5" data-testid="task-detail-progress-editor">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={prog}
                    onChange={(e) => setProg(Number(e.target.value))}
                    className="w-full cursor-pointer accent-[#00FF66]"
                    data-testid="task-detail-progress-slider"
                  />
                  <div className="grid grid-cols-5 gap-2">
                    {[0, 25, 50, 75, 100].map((v) => (
                      <button
                        key={v}
                        onClick={() => setProg(v)}
                        className={`rounded-lg py-1.5 text-center text-[11px] font-bold transition-all duration-150 ${prog === v ? "bg-[#00FF66] text-black shadow-[0_0_16px_rgba(0,255,102,0.3)]" : "border border-white/10 bg-white/5 text-white/55 hover:border-white/20 hover:text-white"}`}
                        data-testid={`task-detail-progress-preset-${v}`}
                      >
                        {v}%
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-[1fr_7rem] gap-2 pt-0.5">
                    <button
                      onClick={saveProgress}
                      disabled={saving}
                      className="flex items-center justify-center gap-1.5 rounded-full bg-[#00FF66] py-2.5 text-xs font-bold text-black shadow-[0_0_24px_rgba(0,255,102,0.25)] transition-colors duration-200 hover:bg-[#00cc52] disabled:opacity-60"
                      data-testid="task-detail-progress-save"
                    >
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      Save
                    </button>
                    <button
                      onClick={() => { setEditing(false); setProg(task.progress ?? 0); }}
                      className="rounded-full border border-white/15 py-2.5 text-xs font-bold text-white/60 transition-colors duration-200 hover:border-white/30 hover:text-white"
                      data-testid="task-detail-progress-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/[0.06] shadow-[inset_0_1px_2px_rgba(0,0,0,0.4)]">
                  <div
                    className="h-full rounded-full transition-[width] duration-500"
                    style={{ width: `${prog}%`, backgroundColor: statusColor, boxShadow: `0 0 12px ${statusColor}66` }}
                  />
                </div>
              )}
            </div>

            {/* Review workflow */}
            <ReviewPanel
              task={task}
              canEdit={canEdit}
              onSaved={onSaved}
              onTaskPatched={(d) => { setStatus(d.status); if (typeof d.progress === "number") setProg(d.progress); }}
              liveStatus={status}
              liveTask={liveTask}
              liveProgress={prog}
            />

            {/* Latest note + discussion */}
            <div className="mt-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4 sm:p-5">
              <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">
                <StickyNote className="h-3.5 w-3.5" />Latest note
              </p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-white/65" data-testid="task-detail-note">
                {task.notes || "No note yet."}
              </p>
              <motion.button
                onClick={() => { setHandingOff(true); handoff.at = Date.now(); onOpenDiscussion(task); }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full border border-[#00FF66]/30 bg-[#00FF66]/10 py-2.5 text-xs font-bold text-[#00FF66] transition-colors duration-200 hover:bg-[#00FF66]/20"
                data-testid="task-detail-open-discussion"
              >
                <MessageSquare className="h-4 w-4" />
                Open discussion{replyCount > 0 ? ` · ${replyCount}` : ""}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
